import { Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { usePricingPagePackages } from '../../api/catalog';
import { toMajor } from '../../contracts/money';
import { useCms, cmsText } from '../../api/cms';
import { SINGLE_FALLBACKS } from '../../content/fallbacks';

/**
 * Live plans strip for the marketing pages — reads the real published catalog
 * (GET /pricing, host-aware) so the strip always reflects the current packages
 * + prices, never a hardcoded list. On a reseller host that's the RESELLER's
 * own catalog. Embedded by Company.tsx (`company.plansPreview`),
 * PlatformLanding.tsx (`product.plansPreview`), and the reseller Landing.tsx
 * (`landing.plansPreview`) — each page owns its own CMS override for the one
 * editable field (the headline).
 *
 * Interval-aware: a card shows its monthly price when it has one, else its
 * yearly price with a /yr unit (reseller catalogs are often yearly-only — a
 * hard "/mo" here rendered "৳—/mo" for every Ogrogoti plan).
 */
export function PlansPreview({ sectionKey }: { sectionKey: 'company.plansPreview' | 'product.plansPreview' | 'landing.plansPreview' }) {
  const { data: plans = [] } = usePricingPagePackages();
  const { data: cms } = useCms();
  if (plans.length === 0) return null;

  const monthlies = plans.map((p) => p.priceMonthlyMinor).filter((m): m is number => typeof m === 'number' && m > 0);
  const yearlies = plans.map((p) => p.priceYearlyMinor).filter((y): y is number => typeof y === 'number' && y > 0);
  const cheapest = monthlies.length
    ? { minor: Math.min(...monthlies), unit: 'mo' }
    : yearlies.length
      ? { minor: Math.min(...yearlies), unit: 'yr' }
      : null;
  const headline = cmsText(cms, sectionKey, 'headline', SINGLE_FALLBACKS[sectionKey]!.headline!);

  return (
    <section id="plans" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
      <div className="text-center max-w-2xl mx-auto">
        <p className="text-xs font-semibold uppercase tracking-wider text-brand">Plans</p>
        <h2 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
          {headline}
        </h2>
        <p className="mt-3 text-zinc-600 dark:text-zinc-300 leading-relaxed">
          {plans.length} plans in taka{cheapest != null ? <> — from <span className="font-semibold text-zinc-900 dark:text-zinc-100">৳{toMajor(cheapest.minor).toLocaleString('en-US')}/{cheapest.unit}</span></> : ''}. See what each plan includes on the pricing page.
        </p>
      </div>

      <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {plans.map((p) => {
          const useMonthly = typeof p.priceMonthlyMinor === 'number' && p.priceMonthlyMinor > 0;
          const minor = useMonthly ? p.priceMonthlyMinor! : p.priceYearlyMinor;
          return (
            <div key={p.id} className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 p-4 text-center">
              <div className="text-sm font-semibold text-zinc-950 dark:text-zinc-50">{p.name}</div>
              <div className="mt-1.5 flex items-baseline justify-center gap-0.5">
                <span className="text-xl font-semibold tabular-nums text-zinc-950 dark:text-zinc-50">
                  ৳{typeof minor === 'number' && minor > 0 ? toMajor(minor).toLocaleString('en-US') : '—'}
                </span>
                <span className="text-xs text-zinc-500 dark:text-zinc-400">/{useMonthly ? 'mo' : 'yr'}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 text-center">
        <Link
          to="/pricing"
          className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-sm font-medium border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
        >
          See full pricing <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </section>
  );
}
